import { useState } from 'react'
import { 
  BellIcon, 
  CheckCircleIcon,
  ClockIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid'
import { useNotifications } from '../domains/notifications/NotificationContext'

interface ReceivedNotificationsProps {
  user: any
}

export default function ReceivedNotifications({ user }: ReceivedNotificationsProps) {
  const { notifications, loading, markAsRead, markAllAsRead } = useNotifications()
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all')
  const [error, setError] = useState<string | null>(null)

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      setError(null)
      console.log('🔔 Attempting to mark notification as read:', notificationId)
      await markAsRead(notificationId)
      console.log('🔔 Successfully marked notification as read:', notificationId)
    } catch (error: any) {
      console.error('🔔 Error marking notification as read:', error)
      console.error('🔔 Error details:', error.response?.data)
      console.error('🔔 Error status:', error.response?.status)
      setError(error.response?.data?.error || error.message || 'Failed to mark notification as read')
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      setError(null)
      console.log('🔔 Attempting to mark all notifications as read')
      await markAllAsRead()
      console.log('🔔 Successfully marked all notifications as read')
    } catch (error: any) {
      console.error('🔔 Error marking all notifications as read:', error)
      console.error('🔔 Error details:', error.response?.data)
      console.error('🔔 Error status:', error.response?.status)
      setError(error.response?.data?.error || error.message || 'Failed to mark all notifications as read')
    }
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
    return date.toLocaleDateString()
  }

  const getTargetDescription = (notification: any) => {
    switch (notification.targetType) {
      case 'ALL_ADMINS':
        return 'All admin users'
      case 'ORGANIZATION_ADMINS':
        return 'Organization admins'
      case 'SPECIFIC_USERS':
        return `${notification.targetEmails.length} specific user(s)`
      default:
        return 'Unknown target'
    }
  }

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') return !notification.isRead
    if (filter === 'read') return notification.isRead
    return true
  })

  const unreadCount = notifications.filter(n => !n.isRead).length

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-6 pb-8 space-y-6 sm:space-y-8 animate-fade-in">
      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}
      
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-glow-sm">
              <BellIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-display-sm font-display text-gradient">Received Notifications</h1>
              <p className="text-body text-gray-600 dark:text-tech-400 font-mono">
                View and manage your system notifications
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <p className="text-sm text-gray-500 dark:text-tech-400">Total</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-tech-100">
                {notifications.length}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500 dark:text-tech-400">Unread</p>
              <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {unreadCount}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-gray-700 dark:bg-tech-800 dark:text-tech-300 hover:bg-gray-200 dark:hover:bg-tech-700'
            }`}
          >
            All ({notifications.length})
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'unread'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-gray-700 dark:bg-tech-800 dark:text-tech-300 hover:bg-gray-200 dark:hover:bg-tech-700'
            }`}
          >
            Unread ({unreadCount})
          </button>
          <button
            onClick={() => setFilter('read')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'read'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-gray-700 dark:bg-tech-800 dark:text-tech-300 hover:bg-gray-200 dark:hover:bg-tech-700'
            }`}
          >
            Read ({notifications.length - unreadCount})
          </button>
        </div>

        <div className="flex items-center space-x-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="flex items-center space-x-2 px-4 py-2 bg-success text-white rounded-lg text-sm font-medium hover:bg-success/90 transition-colors"
            >
              <EyeIcon className="h-4 w-4" />
              <span>Mark all as read</span>
            </button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-4 max-w-5xl">
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-12">
            <BellIcon className="h-16 w-16 text-gray-300 dark:text-tech-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-tech-100 mb-2">
              {filter === 'all' ? 'No notifications yet' : `No ${filter} notifications`}
            </h3>
            <p className="text-gray-500 dark:text-tech-400">
              {filter === 'all' 
                ? 'You\'ll see notifications here when they arrive'
                : `You don't have any ${filter} notifications at the moment`
              }
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`premium-card border-l-4 p-6 ${
                !notification.isRead 
                  ? 'border-l-primary-500 bg-primary-50 dark:bg-primary-900/20 ring-2 ring-primary-200 dark:ring-primary-800' 
                  : 'border-l-gray-300 dark:border-tech-600 bg-white dark:bg-tech-800'
              }`}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  {!notification.isRead ? (
                    <ExclamationTriangleIcon className="h-6 w-6 text-primary-500" />
                  ) : (
                    <CheckCircleSolidIcon className="h-6 w-6 text-success" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-3">
                        <h3 className={`text-lg font-semibold ${
                          notification.isRead 
                            ? 'text-gray-900 dark:text-tech-100' 
                            : 'text-gray-900 dark:text-tech-100'
                        }`}>
                          {notification.subject}
                        </h3>
                        {!notification.isRead && (
                          <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0"></div>
                        )}
                      </div>
                      
                      <p className="text-gray-600 dark:text-tech-400 mt-2 mb-4">
                        {notification.message}
                      </p>
                      
                      <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-tech-400">
                        <div className="flex items-center">
                          <ClockIcon className="w-4 h-4 mr-1" />
                          {formatTime(notification.sentAt)}
                        </div>
                        <div>
                          From: Connect Management
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end space-y-2 ml-4 flex-shrink-0">
                      <div className="flex items-center space-x-1">
                        {notification.isRead ? (
                          <EyeIcon className="h-4 w-4 text-gray-400" />
                        ) : (
                          <EyeSlashIcon className="h-4 w-4 text-primary-500" />
                        )}
                        <span className="text-xs text-gray-500 dark:text-tech-400 whitespace-nowrap">
                          {notification.isRead ? 'Read' : 'Unread'}
                        </span>
                      </div>
                      
                      {!notification.isRead && (
                        <button
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="p-1 text-gray-400 hover:text-success transition-colors"
                          title="Mark as read"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
} 